import { useState, useRef, useMemo, useEffect } from 'react'
import { createPortal } from 'react-dom'
import VoiceRecorder from './VoiceRecorder'
import PollComposerModal from './PollComposerModal'
import { useQuery } from '@tanstack/react-query'
import ComposerAttachments from './ComposerAttachments'
import { toast } from '../../stores/uiStore'
import { Send, Paperclip, AtSign, Smile, ChevronDown, Megaphone, MessagesSquare, Lightbulb, Bell, MessageSquare, Plus, Clock, FileEdit, X, Mic, BarChart3, Pencil, Check } from 'lucide-react'
import { chatService } from '../../services/chatService'
import MentionPanel from './MentionPanel'
import RichTextInput from './RichTextInput'
import { tasksService } from '../../services/tasksService'
import EmojiPicker from './EmojiPicker'
import SlashMenu from './SlashMenu'
import SchedulePicker from './SchedulePicker'
import { avatarGradient, initials } from '../crm/pipeline/tokens'

const POST_PLACEHOLDER = {
  announcement: 'Duyuruyu paylaş… (karar alındı, herkes bilsin)',
  discussion: 'Tartışmayı aç… (ekipten görüş bekleniyor)',
  idea: 'Fikrini yaz… (ham düşünce de olur)',
  update: 'Durumu güncelle… (iş nerede, ne bekliyor)',
}

const TYPES = [
  { v: '', label: 'Mesaj', icon: MessageSquare },
  { v: 'announcement', label: 'Duyuru', icon: Megaphone },
  { v: 'discussion', label: 'Tartışma', icon: MessagesSquare },
  { v: 'idea', label: 'Fikir', icon: Lightbulb },
  { v: 'update', label: 'Güncelleme', icon: Bell },
]

// Mesaj yazma kutusu — tür seçici (Post), @mention, dosya, emoji; Enter gönder / Shift+Enter satır
export default function Composer({ company, channelId, placeholder, onSend, compact, onAi, onTaskPick, replyTo, onCancelReply, onSendVoice, onTyping, onCreatePoll, editing, onSaveEdit, onCancelEdit, prefill }) {
  const [body, setBody] = useState('')
  const [postType, setPostType] = useState('')
  const [postTitle, setPostTitle] = useState('')
  const [typeOpen, setTypeOpen] = useState(null)   // {left, bottom} — portal (ata overflow'u kırpmasın)
  const [mention, setMention] = useState(null) // {q, start}
  const [taskQ, setTaskQ] = useState(null)     // {q, start} — @@ ile görev arama
  const [panel, setPanel] = useState(null)    // ClickUp @ paneli {kind, q, start, len}
  const [files, setFiles] = useState([])       // gönderimde yüklenecek dosyalar
  const [emoji, setEmoji] = useState(null)     // {x,y}
  const [drag, setDrag] = useState(false)
  const [recording, setRecording] = useState(false)
  const [pollOpen, setPollOpen] = useState(false)
  const [schedule, setSchedule] = useState(null)  // {x, y} — zamanlama seçici konumu
  const [sendMenu, setSendMenu] = useState(null)   // {left, bottom} — sonra gönder / taslak
  const sendBtnRef = useRef(null)
  const [slash, setSlash] = useState(null)    // {x, y, q, slashStart} — '+' veya '/' menüsü
  const plusBtnRef = useRef(null)
  const bodyRef = useRef('')
  bodyRef.current = body
  const taRef = useRef(null)
  // Satır içi düzenleme: "Düzenle" denince ayrı popup AÇMAK yerine bu composer'a
  // düşer. Mesaj gövdesi kutuya yüklenir, tür/dosya/anket devre dışı; etiketleme
  // (@ / @@ / @@@ / #), emoji, biçim kısayolları normal composer ile AYNI çalışır.
  useEffect(() => {
    if (editing?.id != null) {
      setBody(editing.body || '')
      // tür/ek/alıntı gibi kalıntılar düzenlemeye karışmasın
      setPostType(''); setPostTitle(''); setFiles([])
      setTimeout(() => {
        const ta = taRef.current
        if (ta) { ta.focus(); const n = (editing.body || '').length; ta.setSelectionRange(n, n) }
      }, 0)
    }
  }, [editing?.id])  // eslint-disable-line react-hooks/exhaustive-deps
  // Dışarıdan hazır metin (ör. yaşlandırmada "bu fatura neden ödenmiyor?"). Kutuya
  // yazılır, GÖNDERİLMEZ — kullanıcı düzenleyip kendisi gönderir.
  useEffect(() => {
    if (!prefill?.text) return
    setBody(prefill.text)
    setTimeout(() => {
      const ta = taRef.current
      if (ta) { ta.focus(); const n = prefill.text.length; ta.setSelectionRange(n, n) }
    }, 0)
  }, [prefill?.nonce])  // eslint-disable-line react-hooks/exhaustive-deps
  const fileRef = useRef(null)
  const typeBtnRef = useRef(null)
  const mentionAnchorRef = useRef(null)
  const addFiles = (list) => {
    const all = Array.from(list || [])
    const arr = all.filter((f) => f.size <= 25 * 1024 * 1024)
    // Sessizce düşürme: kullanıcı dosyayı ekledim sanıp gönderiyordu.
    const tooBig = all.length - arr.length
    if (tooBig > 0) {
      toast.warning('Dosya çok büyük',
        `${tooBig} dosya eklenmedi — sınır 25 MB.`)
    }
    if (arr.length) setFiles((p) => [...p, ...arr])
  }

  /**
   * Yapıştırma — Claude Desktop davranışı:
   *  • Pano'da dosya varsa ek olarak al. Ekran görüntüsü "image.png" gibi
   *    isimsiz geldiği için okunur bir ad üretilir.
   *  • Word/Excel'den kopyalanan UZUN metin composer'ı şişirmesin diye .txt
   *    ekine dönüşür. Eşik yüksek (4000 karakter) — normal paragraf
   *    yapıştırmak metin olarak kalsın, sürpriz olmasın.
   */
  const handlePaste = (e) => {
    const cb = e.clipboardData
    if (!cb) return

    const pasted = Array.from(cb.files || [])
    if (pasted.length) {
      e.preventDefault()
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
      addFiles(pasted.map((f, i) => {
        if (f.name && f.name !== 'image.png') return f
        const ext = (f.type.split('/')[1] || 'png').replace('jpeg', 'jpg')
        return new File([f], `gorsel-${stamp}${pasted.length > 1 ? `-${i + 1}` : ''}.${ext}`,
                        { type: f.type })
      }))
      return
    }

    const text = cb.getData('text/plain') || ''
    if (text.length > 4000) {
      e.preventDefault()
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
      const file = new File([text], `yapistirilan-metin-${stamp}.txt`, { type: 'text/plain' })
      addFiles([file])
      toast.info('Uzun metin eklendi',
        `${text.length.toLocaleString('tr-TR')} karakter dosya olarak eklendi.`)
    }
    // Kısa metin: varsayılan yapıştırma sürsün.
  }

  const { data } = useQuery({
    queryKey: ['chat-users', company], queryFn: () => chatService.searchUsers(company), staleTime: 300000,
  })
  const users = data?.data?.users || []
  const { data: taskData } = useQuery({
    queryKey: ['chat-task-search', company, taskQ?.q || ''],
    queryFn: () => tasksService.taskSearch(company, taskQ?.q || ''),
    enabled: !!taskQ, staleTime: 30000,
  })
  const taskList = (taskData?.data?.items || taskData?.data?.tasks || []).slice(0, 6)
  const mentionList = useMemo(() => {
    if (!mention) return []
    const t = (mention.q || '').toLocaleLowerCase('tr')
    return users.filter((u) => !t || (u.display_name || '').toLocaleLowerCase('tr').includes(t)
      || (u.canias_username || '').toLowerCase().includes(t)).slice(0, 6)
  }, [mention, users])

  const onChange = (e) => {
    const v = e.target.value
    setBody(v)
    if (v.trim()) onTyping?.()   // kısıtlama sahibinde (3 sn) — her tuşta istek atılmaz
    const upto = v.slice(0, e.target.selectionStart)
    // ClickUp kısayolları: @ kişi · @@ görev · @@@ belge(teklif) · # kanal
    const doc3 = upto.match(/@@@([^\s@]*)$/)
    const task2 = !doc3 && upto.match(/@@([^\s@]*)$/)
    const user1 = !doc3 && !task2 && upto.match(/@([A-Za-z0-9_.\-]*)$/)
    const chan = !doc3 && !task2 && !user1 && upto.match(/(?:^|\s)#([^\s#]*)$/)
    const hit = doc3 || task2 || user1 || chan
    if (hit) {
      const kind = doc3 ? 'quote' : task2 ? 'task' : user1 ? 'user' : 'channel'
      const token = hit[0].replace(/^\s/, '')
      setPanel({ kind, q: hit[1] || '', start: upto.length - token.length, len: token.length })
    } else setPanel(null)
    setTaskQ(null); setMention(null)
    // satır başında '/' → komut menüsü (ClickUp slash command)
    const sm = upto.match(/(?:^|\n)\/([^\n]*)$/)
    if (sm && !slash) {
      const r = e.target.getBoundingClientRect()
      setSlash({ x: r.left + 8, y: r.top - 8, q: sm[1], slashStart: upto.length - sm[1].length - 1 })
    } else if (!sm && slash) {
      setSlash(null)
    }
  }
  const pickMention = (u) => {
    const uname = u.canias_username || (u.display_name || '').replace(/\s+/g, '')
    const before = body.slice(0, mention.start)
    const after = body.slice(taRef.current?.selectionStart ?? body.length)
    setBody(`${before}@${uname} ${after}`)
    setMention(null)
    taRef.current?.focus()
  }
  // Markdown ekleme — seçili metni sarar; block=true ise satır başına yazar
  const applyIns = (ins, strip) => {
    const ta = taRef.current
    // '/' komutuyla açıldıysa önce '/xyz' parçasını çıkar → tek state güncellemesi
    let body = bodyRef.current
    let caret = ta?.selectionStart ?? body.length
    let caretEnd = ta?.selectionEnd ?? caret
    if (strip && strip.end > strip.start) {
      body = body.slice(0, strip.start) + body.slice(strip.end)
      caret = caretEnd = strip.start
    }
    const start = caret
    const end = caretEnd
    const sel = body.slice(start, end)
    const before = ins.before || ''
    const after = ins.after || ''
    let insertAt = start
    let text = `${before}${sel}${after}`
    if (ins.block) {
      const lineStart = body.lastIndexOf('\n', start - 1) + 1
      insertAt = lineStart
      const needsNl = lineStart > 0 && body[lineStart - 1] !== '\n'
      text = `${needsNl ? '\n' : ''}${before}${body.slice(lineStart, end)}${after}`
      const next = body.slice(0, lineStart) + text + body.slice(end)
      setBody(next)
      setTimeout(() => { ta?.focus(); const p = lineStart + text.length; ta?.setSelectionRange(p, p) }, 0)
      return
    }
    const next = body.slice(0, insertAt) + text + body.slice(end)
    setBody(next)
    setTimeout(() => {
      ta?.focus()
      const p = sel ? insertAt + text.length : insertAt + before.length
      ta?.setSelectionRange(p, p)
    }, 0)
  }

  const onSlashPick = (item) => {
    // '/' ile açıldıysa yazılan '/xyz' parçasını temizle (menü içi arama textarea'yı değiştirmez)
    const strip = slash?.slashStart != null
      ? { start: slash.slashStart, end: slash.slashStart + 1 + (slash.q || '').length }
      : null
    if (item.ins) return applyIns(item.ins, strip)
    if (strip) setBody(bodyRef.current.slice(0, strip.start) + bodyRef.current.slice(strip.end))
    if (item.act === 'file') return fileRef.current?.click()
    if (item.act === 'emoji') {
      const r = taRef.current?.getBoundingClientRect()
      return setEmoji({ x: (r?.left || 200) + 20, y: (r?.top || 200) - 340 })
    }
    if (item.act === 'task') return onTaskPick ? onTaskPick() : applyIns({ before: '@@' })
    if (item.act === 'ai') return onAi?.()
    if (item.act?.startsWith('post:')) return setPostType(item.act.slice(5))
  }

  // Panelden seçim: tetikleyici metni (@xx / @@xx / #xx) seçilen öğenin insert metniyle değiştir
  const pickMentionItem = (it) => {
    const ta = taRef.current
    const cur = bodyRef.current
    const start = panel?.start ?? cur.length
    const end = Math.min(cur.length, start + (panel?.len ?? 0))
    const next = `${cur.slice(0, start)}${it.insert} ${cur.slice(end)}`
    setBody(next)
    setPanel(null)
    setTimeout(() => { ta?.focus(); const p = start + it.insert.length + 1; ta?.setSelectionRange(p, p) }, 0)
  }

  const pickTask = (t) => {
    const ta = taRef.current
    const before = body.slice(0, taskQ.start)
    const after = body.slice(ta?.selectionStart ?? body.length)
    setBody(`${before}@@${t.id} ${after}`)
    setTaskQ(null)
    setTimeout(() => ta?.focus(), 0)
  }

  const cancelEdit = () => { setBody(''); onCancelEdit?.() }

  const send = (extra = {}) => {
    // Düzenleme modu: yeni mesaj göndermek yerine mevcut mesajı güncelle.
    if (editing?.id != null) {
      const t = body.trim()
      if (!t) return
      onSaveEdit?.(editing.id, t)
      setBody('')
      return
    }
    if (!body.trim() && files.length === 0) return
    const text = body.trim()
    // Yalnız ek: boş gövde + allow_empty (eski '(dosya)' yer tutucu kaldırıldı)
    onSend?.({
      body: text,
      allow_empty: !text && files.length > 0,
      post_type: postType || null,
      post_title: postType ? (postTitle.trim() || null) : null,
      ...extra,
    }, files)
    setBody(''); setPostTitle(''); setMention(null); setTaskQ(null); setFiles([]); setPostType('')
  }
  const cur = TYPES.find((t) => t.v === postType) || TYPES[0]

  return (
    <div ref={mentionAnchorRef} className="p-3 border-t border-gray-100 bg-white shrink-0 relative z-30"
      onDragOver={(e) => { e.preventDefault(); setDrag(true) }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files) }}
      onPaste={handlePaste}>
      {pollOpen && (
        <PollComposerModal
          onClose={() => setPollOpen(false)}
          onSubmit={async (cfg) => { setPollOpen(false); await onCreatePoll?.(cfg) }} />
      )}
      {/* Kayıt açıkken metin alanı yerine kaydedici (WhatsApp deseni).
          Yanıt bandı korunur — sesli mesajla da alıntı yapılabilir. */}
      {recording && (
        <VoiceRecorder
          onCancel={() => setRecording(false)}
          onDone={(file, ms, wave) => {
            setRecording(false)
            onSendVoice?.(file, ms, wave)
          }} />
      )}
      {/* Düzenleme bandı — hangi mesajı düzenlediğin composer'ın üstünde durur.
          Ayrı popup yok; kutu doğrudan düzenleme moduna geçer. */}
      {editing && (
        <div className="mb-2 flex items-center gap-2 border-l-[3px] border-amber-500 bg-amber-50 rounded-r-lg pl-2.5 pr-1 py-1.5">
          <Pencil size={13} className="text-amber-600 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-[11.5px] font-semibold text-amber-700">Mesajı düzenle</div>
            <div className="text-[11px] text-amber-600/80">Enter ile kaydet · Esc ile vazgeç</div>
          </div>
          <button type="button" onClick={cancelEdit} title="Düzenlemeyi iptal et"
            className="p-1 rounded-md text-amber-500 hover:text-amber-700 hover:bg-amber-100 shrink-0">
            <X size={14} />
          </button>
        </div>
      )}
      {/* Yanıt bandı — neye yanıt verdiğin composer'ın üstünde durur */}
      {!editing && replyTo && (
        <div className="mb-2 flex items-start gap-2 border-l-[3px] border-primary-500 bg-gray-50 rounded-r-lg pl-2.5 pr-1 py-1.5">
          <div className="min-w-0 flex-1">
            <div className="text-[11.5px] font-semibold text-primary-600 truncate">
              {replyTo.author_name}
            </div>
            <div className="text-[12px] text-gray-500 truncate">
              {(replyTo.body || '').trim() || ((replyTo.attachments || []).length ? '📎 Ek' : '')}
            </div>
          </div>
          <button type="button" onClick={onCancelReply} title="Yanıtı iptal et"
            className="p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 shrink-0">
            <X size={14} />
          </button>
        </div>
      )}
      {panel && mentionAnchorRef.current && (
        <MentionPanel company={company} anchor={mentionAnchorRef.current} channelId={channelId}
          initialKind={panel.kind} initialQuery={panel.q}
          onPick={pickMentionItem} onClose={() => setPanel(null)} />
      )}
      {false && mention && mentionList.length > 0 && mentionAnchorRef.current && createPortal(
        <div style={{
            left: mentionAnchorRef.current.getBoundingClientRect().left + 12,
            width: mentionAnchorRef.current.getBoundingClientRect().width - 24,
            bottom: window.innerHeight - mentionAnchorRef.current.getBoundingClientRect().top + 4,
          }}
          className="fixed bg-white border border-gray-200 rounded-xl shadow-xl py-1 max-h-52 overflow-y-auto z-[91]">
          {mentionList.map((u) => (
            <button key={u.id} onClick={() => pickMention(u)} className="w-full flex items-center gap-2 px-2.5 py-1.5 hover:bg-gray-50 text-left">
              <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${avatarGradient(u.display_name)} flex items-center justify-center text-[8px] font-semibold text-white`}>
                {initials(u.display_name)}
              </div>
              <span className="text-[12px] text-gray-800 truncate">{u.display_name}</span>
              <span className="text-[10px] text-gray-400 ml-auto">@{u.canias_username}</span>
            </button>
          ))}
        </div>, document.body)}

      {false && taskQ && taskList.length > 0 && mentionAnchorRef.current && createPortal(
        <div style={{
            left: mentionAnchorRef.current.getBoundingClientRect().left + 12,
            width: mentionAnchorRef.current.getBoundingClientRect().width - 24,
            bottom: window.innerHeight - mentionAnchorRef.current.getBoundingClientRect().top + 4,
          }}
          className="fixed bg-white border border-gray-200 rounded-xl shadow-xl py-1 max-h-52 overflow-y-auto z-[91]">
          <div className="px-2.5 py-1 text-[10px] font-semibold text-gray-400 uppercase">Görevler</div>
          {taskList.map((t) => (
            <button key={t.id} onClick={() => pickTask(t)} className="w-full flex items-center gap-2 px-2.5 py-1.5 hover:bg-gray-50 text-left">
              <span className="text-[10px] text-violet-500 font-mono shrink-0">#{t.id}</span>
              <span className="text-[12px] text-gray-800 truncate flex-1">{t.title || t.name}</span>
              {t.list_name && <span className="text-[10px] text-gray-400 shrink-0">{t.list_name}</span>}
            </button>
          ))}
        </div>, document.body)}

      <div className={`border rounded-xl focus-within:ring-1 focus-within:ring-violet-200 overflow-hidden ${drag ? 'border-violet-400 bg-violet-50/40' : 'border-gray-200'}`}>
        <ComposerAttachments
          files={files}
          onRemove={(i) => setFiles((p) => p.filter((_, j) => j !== i))}
        />
        {postType && (
          <input value={postTitle} onChange={(e) => setPostTitle(e.target.value)} placeholder={`${cur.label} başlığı…`}
            className="w-full px-3 pt-2.5 pb-1 text-[14px] font-semibold outline-none" />
        )}
        <RichTextInput ref={taRef} value={body} onChange={onChange} rows={compact ? 2 : 3}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && editing) { e.preventDefault(); cancelEdit(); return }
            if (e.key === 'Enter' && !e.shiftKey && !slash) { e.preventDefault(); send(); return }
            if ((e.metaKey || e.ctrlKey) && ['b', 'i', 'e'].includes(e.key.toLowerCase())) {
              e.preventDefault()
              const k = e.key.toLowerCase()
              applyIns(k === 'b' ? { before: '**', after: '**' } : k === 'i' ? { before: '*', after: '*' } : { before: '`', after: '`' })
            }
          }}
          placeholder={postType ? POST_PLACEHOLDER[postType] : (placeholder || 'Mesaj yaz… ( / komutlar, @ etiketle, Enter gönder )')}
          className="w-full px-3 py-2 text-sm" />
        <div className="flex items-center gap-1 px-2 pb-2">
          {/* Ekle menüsü ( '/' ile de açılır ) */}
          <button ref={plusBtnRef} onClick={() => {
              if (slash) return setSlash(null)
              const r = plusBtnRef.current.getBoundingClientRect()
              setSlash({ x: r.left, y: r.top - 8, q: '', slashStart: null })
            }}
            title="Ekle ( / )" className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center">
            <Plus size={15} />
          </button>
          <span className="w-px h-4 bg-gray-200 mx-0.5" />
          {/* Tür seçici */}
          <div className="relative">
            <button ref={typeBtnRef} onClick={() => {
                if (typeOpen) return setTypeOpen(null)
                const r = typeBtnRef.current.getBoundingClientRect()
                setTypeOpen({ left: r.left, bottom: window.innerHeight - r.top + 6 })
              }}
              className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 text-[12px] text-gray-700">
              <cur.icon size={12} /> {cur.label} <ChevronDown size={11} />
            </button>
            {typeOpen && createPortal(
              <>
                <div className="fixed inset-0 z-[90]" onClick={() => setTypeOpen(null)} />
                <div style={{ left: typeOpen.left, bottom: typeOpen.bottom }}
                  className="fixed bg-white border border-gray-100 rounded-xl shadow-xl py-1 w-48 z-[91]">
                  {TYPES.map((t, i) => (
                    <button key={t.v} onClick={() => { setPostType(t.v); setTypeOpen(null) }}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-left hover:bg-gray-50 ${t.v === postType ? 'bg-gray-100 font-medium' : ''}`}>
                      <t.icon size={13} className="text-gray-500" /> {t.label}
                      {i === 0 && <span className="ml-auto text-[10px] text-gray-300">varsayılan</span>}
                    </button>
                  ))}
                </div>
              </>, document.body)}
          </div>
          <button onClick={() => fileRef.current?.click()} title="Dosya ekle (sürükle-bırak / ⌘V de olur)" className="p-1.5 text-gray-400 hover:text-violet-600"><Paperclip size={15} /></button>
          <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = '' }} />
          <button onClick={() => {
            const ta = taRef.current
            const at = ta?.selectionStart ?? bodyRef.current.length
            setPanel({ kind: 'user', q: '', start: at, len: 0 })
          }} title="Kişi / görev / belge etiketle" className="p-1.5 text-gray-400 hover:text-violet-600"><AtSign size={15} /></button>
          <button onClick={(e) => setEmoji({ x: e.clientX, y: e.clientY })} title="Emoji" className="p-1.5 text-gray-400 hover:text-violet-600"><Smile size={15} /></button>
          {!editing && onCreatePoll && (
            <button onClick={() => setPollOpen(true)} title="Anket"
              className="p-1.5 text-gray-400 hover:text-violet-600"><BarChart3 size={15} /></button>
          )}
          {/* Sesli mesaj — tarayıcı MediaRecorder desteklemiyorsa düğme çizilmez
              (ölü düğme göstermek, hiç göstermemekten kötü). Düzenlemede kapalı. */}
          {!editing && onSendVoice && typeof window !== 'undefined' && window.MediaRecorder && (
            <button onClick={() => setRecording(true)} disabled={recording}
              title="Sesli mesaj" className="p-1.5 text-gray-400 hover:text-violet-600 disabled:opacity-40">
              <Mic size={15} />
            </button>
          )}
          <div className="ml-auto flex items-center">
            {editing ? (
              // Düzenlemede tek eylem: kaydet. "Sonra gönder / taslak" anlamsız → gizli.
              <>
                <button type="button" onClick={cancelEdit}
                  className="px-2.5 py-2 text-[12px] text-gray-500 hover:bg-gray-100 rounded-lg">İptal</button>
                <button onClick={() => send()} disabled={!body.trim()} title="Kaydet"
                  className="p-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-40"><Check size={15} /></button>
              </>
            ) : (
              <>
                <button onClick={() => send()} disabled={!body.trim() && files.length === 0}
                  className="p-2 bg-violet-600 text-white rounded-l-lg hover:bg-violet-700 disabled:opacity-40"><Send size={15} /></button>
                <button ref={sendBtnRef} disabled={!body.trim() && files.length === 0}
                  onClick={() => {
                    if (sendMenu) return setSendMenu(null)
                    const r = sendBtnRef.current.getBoundingClientRect()
                    setSendMenu({ left: Math.max(8, r.right - 220), bottom: window.innerHeight - r.top + 6 })
                  }}
                  title="Gönderim seçenekleri"
                  className="px-1 py-2 bg-violet-600 text-white rounded-r-lg border-l border-violet-500 hover:bg-violet-700 disabled:opacity-40">
                  <ChevronDown size={13} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      {emoji && (
        <EmojiPicker x={emoji.x} y={emoji.y} keepOpen onClose={() => setEmoji(null)}
          onPick={(e) => setBody((b) => b + e)} />
      )}
      {sendMenu && createPortal(
        <>
          <div className="fixed inset-0 z-[90]" onClick={() => setSendMenu(null)} />
          <div style={{ left: sendMenu.left, bottom: sendMenu.bottom }}
            className="fixed bg-white border border-gray-100 rounded-xl shadow-xl py-1 w-56 z-[91]">
            <button onClick={() => {
                const r = sendBtnRef.current?.getBoundingClientRect()
                setSendMenu(null)
                setSchedule({ x: Math.max(8, (r?.right || 400) - 320), y: (r?.top || 300) - 12 })
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-left hover:bg-gray-50">
              <Clock size={13} className="text-violet-500" /> Sonra gönder…
            </button>
            <button onClick={() => { setSendMenu(null); send({ is_draft: true }) }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-[13px] text-left hover:bg-gray-50">
              <FileEdit size={13} className="text-gray-500" /> Taslak olarak kaydet
            </button>
          </div>
        </>, document.body)}

      {schedule && (
        <SchedulePicker x={schedule.x} y={schedule.y}
          onClose={() => setSchedule(null)}
          onPick={(iso) => send({ scheduled_at: iso })} />
      )}

      {slash && (
        <SlashMenu x={slash.x} y={slash.y} query={slash.q} onPick={onSlashPick} onClose={() => setSlash(null)} />
      )}
    </div>
  )
}
